import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { customersApi } from '@/modules/tenant/services/customersApi';
import type { Customer, CustomerFormData, CustomerFilters } from '../types/customer.types';
import { safeQuery } from '@/lib/queryHelpers';
import { getErrorMessage } from '@marine/utils/getErrorMessage';

/**
 * Query key factory for customers
 * IMPORTANT: Use primitives, not objects, to prevent infinite re-renders
 */
export const customerKeys = {
  all: ['customers'] as const,
  lists: () => [...customerKeys.all, 'list'] as const,
  // Use individual primitive values instead of the entire filter object
  list: (filters?: CustomerFilters) => [
    ...customerKeys.lists(),
    filters?.page ?? null,
    filters?.per_page ?? null,
    filters?.customer_type ?? null,
    filters?.search ?? null,
  ] as const,
  details: () => [...customerKeys.all, 'detail'] as const,
  detail: (id: string) => [...customerKeys.details(), id] as const,
  search: (query: string) => [...customerKeys.all, 'search', query] as const,
};

/**
 * Hook to fetch paginated customers list
 * Uses safeQuery to prevent infinite re-renders from error state
 */
export function useCustomers(filters?: CustomerFilters) {
  return useQuery({
    queryKey: customerKeys.list(filters),
    queryFn: () => safeQuery(
      () => customersApi.getCustomers(filters),
      { items: [], meta: { total: 0, per_page: 15, current_page: 1, last_page: 1 } }
    ),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch a single customer
 * Uses safeQuery to prevent infinite re-renders from error state
 */
export function useCustomer(id: string) {
  return useQuery({
    queryKey: customerKeys.detail(id),
    queryFn: () => safeQuery(
      () => customersApi.getCustomer(id),
      {} as Partial<Customer>
    ),
    enabled: !!id,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to create a customer
 */
export function useCreateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CustomerFormData) => customersApi.createCustomer(data),
    onSuccess: () => {
      // Invalidate and refetch customers list
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      
      // Show success message
      toast.success('Customer created successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to create customer'));
    },
  });
}

/**
 * Hook to update a customer
 */
export function useUpdateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CustomerFormData> }) =>
      customersApi.updateCustomer(id, data),
    onSuccess: (updatedCustomer) => {
      // Update the customer in the cache
      queryClient.setQueryData(
        customerKeys.detail(updatedCustomer.id),
        updatedCustomer
      );
      
      // Invalidate lists to refresh
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      
      toast.success('Customer updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update customer'));
    },
  });
}

/**
 * Hook to delete a customer
 */
export function useDeleteCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => customersApi.deleteCustomer(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: customerKeys.detail(deletedId) });
      
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      
      toast.success('Customer deleted successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to delete customer'));
    },
  });
}

/**
 * Hook to restore a customer
 */
export function useRestoreCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => customersApi.restoreCustomer(id),
    onSuccess: (customer) => {
      queryClient.invalidateQueries({ queryKey: customerKeys.detail(customer.id) });
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      toast.success('Customer restored successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to restore customer'));
    },
  });
}

/**
 * Hook to search customers
 * Uses safeQuery to prevent infinite re-renders from error state
 */
export function useSearchCustomers(query: string, enabled = true) {
  return useQuery({
    queryKey: customerKeys.search(query),
    queryFn: () => safeQuery(
      () => customersApi.searchCustomers(query),
      { items: [], meta: { total: 0, per_page: 15, current_page: 1, last_page: 1 } }
    ),
    enabled: enabled && query.length >= 2,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
